import React, { useEffect, useMemo, useState } from 'react';
import secureStorage from '../utils/secureStorage';
import axios from 'axios';
import { AlertCircle, CheckCircle, Edit, FileText, Plus, Printer, Shield, Trash, Trash2, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { API_BASE_URL } from '../config';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const SafetyModule = ({ theme, exerciseId, printIcs208Combined }) => {
  const [safetyData, setSafetyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('incident-information');

  // Helper function to format timestamp in 24-hour format (e.g., "2024-10-27 23:15")
  const format24HourTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    category: 'incident-information',
    title: '',
    description: '',
    priority: 'medium',
    status: 'active',
    timestamp: '',
    location: '',
    contact_name: '',
    contact_phone: '',
    notes: '',
    site_safety_plan_required: false
  });

  const [errors, setErrors] = useState({});
  // Location management state
  const [locations, setLocations] = useState([]);
  const [showAddLocationDialog, setShowAddLocationDialog] = useState(false);
  const [newLocationName, setNewLocationName] = useState('');
  const [newLocationDescription, setNewLocationDescription] = useState('');

  const defaultLocations = [
    'Command Post',
    'Hospital',
    'Fire Station',
    'Police Station',
    'Operations Center EOC',
    'Staging Area'
  ];

  // Dynamic location options - combine default and custom locations
  const locationOptions = React.useMemo(() => {
    // Start with default locations
    let options = [...defaultLocations];
    
    // Add custom locations
    if (locations.length > 0) {
      const customLocationNames = locations.map(location => location.name);
      options = [...options, ...customLocationNames];
    }
    
    // Ensure the current location value is included if editing
    if (editingEntry && editingEntry.location && !options.includes(editingEntry.location)) {
      options = [editingEntry.location, ...options];
    }
    
    return options;
  }, [locations, editingEntry?.location]);

  // Categories and their fields
  const categories = {
    'incident-information': {
      label: 'Incident Information',
      icon: AlertCircle,
      fields: []
    },
    'safety-planning': {
      label: 'Safety Planning',
      icon: Shield,
      fields: [
        { key: 'safety_message', label: 'Safety Message', type: 'textarea', required: true },
        { key: 'safety_plan', label: 'Safety Plan', type: 'textarea', required: true }
      ]
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'critical', label: 'Critical', color: 'text-red-500' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'text-green-500' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-500' },
    { value: 'completed', label: 'Completed', color: 'text-blue-500' },
    { value: 'archived', label: 'Archived', color: 'text-gray-500' }
  ];

  // Load safety data and locations on component mount
  useEffect(() => {
    loadSafetyData();
    fetchLocations();
  }, [exerciseId]);

  const loadSafetyData = async () => {
    if (!exerciseId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/safety-entries?exercise_id=${exerciseId}`);
      setSafetyData(response.data);
    } catch (error) {
      console.error('Error loading safety data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    if (!exerciseId) return; // Don't fetch if no exercise selected
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/map-objects?type=marker&exercise_id=${exerciseId}`, { headers });
      // Map to expected format
      setLocations(response.data.map(marker => ({ id: marker.id, name: marker.name })));
    } catch (error) {
      console.error('Error fetching map markers:', error);
    }
  };

  // Add new location handler
  const handleAddLocation = async () => {
    if (!newLocationName.trim()) {
      alert('Please enter a location name');
      return;
    }

    // Check if location already exists
    if (locations.some(l => l.name.toLowerCase() === newLocationName.toLowerCase()) || 
        defaultLocations.some(d => d.toLowerCase() === newLocationName.toLowerCase())) {
      alert('Location already exists');
      return;
    }

    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const locationData = {
        name: newLocationName,
        description: newLocationDescription.trim() || 'Safety location'
      };
      
      const response = await axios.post(`${API}/locations`, locationData, { headers });
      setLocations(prev => [...prev, response.data]);
      setNewLocationName('');
      setNewLocationDescription('');
      alert(`Location "${newLocationName}" added successfully`);
    } catch (error) {
      console.error('Error adding location:', error);
      alert('Failed to add location. Please try again.');
    }
  };

  // Delete location handler
  const handleDeleteLocation = async (locationId, locationName) => {
    if (formData.location === locationName) {
      alert('Cannot delete currently selected location');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${locationName}"?`)) {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/locations/${locationId}`, { headers });
        setLocations(prev => prev.filter(loc => loc.id !== locationId));
        alert(`Location "${locationName}" deleted successfully`);
      } catch (error) {
        console.error('Error deleting location:', error);
        alert('Failed to delete location');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      category: activeCategory,
      title: '',
      description: '',
      priority: 'medium',
      status: 'active',
      location: '',
      contact_name: '',
      contact_phone: '',
      notes: '',
      site_safety_plan_required: false,
      date_from: '',
      date_to: ''
    });
    setErrors({});
    setEditingEntry(null);
  };

  const validateForm = () => {
    const newErrors = {};
    const categoryFields = categories[formData.category]?.fields || [];

    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';

    // Validate category-specific required fields
    categoryFields.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const entryData = {
        ...formData,
        exercise_id: exerciseId,
        timestamp: formData.timestamp || new Date().toISOString()
      };

      if (editingEntry) {
        await axios.put(`${API}/safety-entries/${editingEntry.id}`, entryData);
      } else {
        await axios.post(`${API}/safety-entries`, entryData);
      }

      resetForm();
      setShowForm(false);
      loadSafetyData();
    } catch (error) {
      console.error('Error saving safety entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      category: entry.category || 'incident-information',
      title: entry.title || '',
      description: entry.description || '',
      priority: entry.priority || 'medium',
      status: entry.status || 'active',
      timestamp: entry.timestamp ? entry.timestamp.slice(0, 16) : '',
      location: entry.location || '',
      contact_name: entry.contact_name || '',
      contact_phone: entry.contact_phone || '',
      notes: entry.notes || '',
      site_safety_plan_required: entry.site_safety_plan_required || false,
      ...Object.fromEntries(
        Object.keys(categories).flatMap(cat => 
          categories[cat].fields.map(field => [field.key, entry[field.key] || ''])
        )
      )
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      setLoading(true);
      const token = secureStorage.getToken();
      await axios.delete(`${API}/safety-entries/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await loadSafetyData();
      alert('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting safety entry:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete this entry. Only Agency Admins and Super Admins can delete entries.');
      } else if (error.response?.status === 404) {
        alert('Entry not found. It may have already been deleted.');
      } else {
        alert('Failed to delete entry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (entry = null) => {
    const printContent = entry ? 
      generateSingleEntryPrint(entry) : 
      generateMultipleEntriesPrint();
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>Safety Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; }
            .header h1 { color: #f59e0b; margin-bottom: 5px; }
            .header h2 { color: #666; margin-bottom: 10px; }
            .section { margin-bottom: 40px; page-break-inside: avoid; }
            .section h2 { color: #f59e0b; border-bottom: 2px solid #f59e0b; padding-bottom: 5px; margin-bottom: 20px; }
            .entry { margin-bottom: 25px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; background: #fafafa; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; display: inline-block; width: 180px; color: #333; }
            .category { background: #f59e0b; color: white; padding: 10px; margin-bottom: 15px; border-radius: 5px; }
            .category h3 { margin: 0; color: white; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
            @media print { 
              .entry { page-break-inside: avoid; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p><strong>Safety Report</strong></p>
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Powered by EXRSIM - Emergency Response Simulator</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
  };

  const generateSingleEntryPrint = (entry) => {
    const category = categories[entry.category];
    return `
      <div class="header">
        <h1>Safety Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
      </div>
      <div class="entry">
        <div class="category">
          <h3>${category?.label || entry.category}</h3>
        </div>
        <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
        <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
        <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
        <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
        <div class="field"><span class="label">Timestamp:</span> ${entry.timestamp ? format24HourTimestamp(entry.timestamp) : 'N/A'}</div>
        <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
        <div class="field"><span class="label">Contact Info:</span> ${entry.contact_info || 'N/A'}</div>
        <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
        ${entry.incident_name ? `<div class="field"><span class="label">Incident Name:</span> ${entry.incident_name}</div>` : ''}
        ${entry.operational_period ? `<div class="field"><span class="label">Operational Period:</span> ${entry.operational_period}</div>` : ''}
        ${entry.safety_message ? `<div class="field"><span class="label">Safety Message:</span> ${entry.safety_message}</div>` : ''}
        ${entry.safety_plan ? `<div class="field"><span class="label">Safety Plan:</span> ${entry.safety_plan}</div>` : ''}
        ${entry.site_safety_plan ? `<div class="field"><span class="label">Site Safety Plan:</span> ${entry.site_safety_plan}</div>` : ''}
        <div class="field"><span class="label">Site Safety Plan Required:</span> ${entry.site_safety_plan_required ? 'Yes' : 'No'}</div>
        ${entry.site_safety_plan_location ? `<div class="field"><span class="label">Site Safety Plan Location:</span> ${entry.site_safety_plan_location}</div>` : ''}
      </div>
    `;
  };

  const generateMultipleEntriesPrint = () => {
    const selectedData = selectedEntries.length > 0 ? 
      safetyData.filter(entry => selectedEntries.includes(entry.id)) : 
      safetyData;
    
    return `
      <div class="header">
        <h1>Safety Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
        <p>${selectedData.length} entry(ies)</p>
      </div>
      ${selectedData.map(entry => generateSingleEntryPrint(entry)).join('')}
    `;
  };

  const toggleEntrySelection = (id) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    );
  };

  const filteredData = activeCategory === 'all' 
    ? (safetyData || []) 
    : (safetyData || []).filter(entry => entry.category === activeCategory);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              Safety Management
            </h1>
            <p className="text-white text-sm">
              Manage incident safety plans, messages, and site safety requirements
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Safety Entry
            </Button>
            {selectedEntries.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedEntries.length})
              </Button>
            )}
            {safetyData.length > 0 && printIcs208Combined && (
              <Button
                onClick={printIcs208Combined}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <FileText className="h-4 w-4 mr-2" />
                Print ICS 208 (Combined)
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = category.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeCategory === key
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <IconComponent className="h-4 w-4 mr-2" />
                {category.label}
              </button>
            );
          })}
          {/* Show All Button */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeCategory === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Shield className={`h-4 w-4 mr-2 ${
              activeCategory === 'all' ? 'text-white' : 'text-orange-600'
            }`} />
            <span>Show All</span>
          </button>
        </div>
      </div>

      {/* Entry Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-orange-500">
              {editingEntry ? 'Edit Safety Entry' : 'Add New Safety Entry'}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmitLocal} className="space-y-6">
            {/* Title Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Title of this entry"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            {/* Emergency Phase Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Emergency Phase *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              >
                {Object.entries(categories).map(([key, category]) => (
                  <option key={key} value={key} className={category.color}>{category.label}</option>
                ))}
              </select>
            </div>

            {/* Removed Incident Name and Timestamp fields */}

            {/* Operational Period: Date/Time From and Date/Time To */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Operational Period - Date / Time from:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_from ? formData.date_from.split('T')[1] : '00:00';
                      setFormData({...formData, date_from: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_from ? formData.date_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_from ? formData.date_from.split('T')[0] : '';
                        setFormData({...formData, date_from: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Date / Time to:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_to ? formData.date_to.split('T')[1] : '00:00';
                      setFormData({...formData, date_to: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_to ? formData.date_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_to ? formData.date_to.split('T')[0] : '';
                        setFormData({...formData, date_to: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Situation Summary *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="a collective description of the current situation"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.description ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Priority and Status */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Name and Contact Phone */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="First and last name"
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={formData.contact_phone}
                  onChange={(e) => {
                    // Only allow digits and auto-format as XXX-XXX-XXXX
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = '';
                    if (digits.length > 0) {
                      formatted = digits.slice(0, 3);
                      if (digits.length > 3) {
                        formatted += '-' + digits.slice(3, 6);
                      }
                      if (digits.length > 6) {
                        formatted += '-' + digits.slice(6, 10);
                      }
                    }
                    if (formatted.length <= 12) {
                      setFormData({...formData, contact_phone: formatted});
                    }
                  }}
                  placeholder="204-123-4567"
                  maxLength="12"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.contact_phone ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
              </div>
            </div>

            {/* Category-specific fields */}
            {categories[formData.category]?.fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-white mb-1">
                  {field.label} {field.required && '*'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                ) : field.type === 'checkbox' ? (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData[field.key] || false}
                      onChange={(e) => setFormData({...formData, [field.key]: e.target.checked})}
                      className="mr-2 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-600 rounded"
                    />
                    <span className="text-white">Required</span>
                  </div>
                ) : (
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                )}
                {errors[field.key] && <p className="text-red-500 text-xs mt-1">{errors[field.key]}</p>}
              </div>
            ))}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="notes / comments / remarks"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Add Entry')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Entries List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-orange-500">
            {activeCategory === 'all' ? 'All Safety Entries' : categories[activeCategory]?.label + ' Entries'}
          </h3>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-white mt-2">Loading entries...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-orange-500 mb-2">
                {activeCategory === 'all' ? 'No Safety Entries Found' : `No ${categories[activeCategory]?.label} Entries`}
              </h3>
              <p className="text-white mb-4">
                {activeCategory === 'all' 
                  ? 'Start by adding your first safety entry in any category for this exercise.'
                  : `Start by adding your first ${categories[activeCategory]?.label.toLowerCase()} entry for this exercise.`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredData.map((entry) => {
                const priorityOption = priorityOptions.find(p => p.value === entry.priority);
                const statusOption = statusOptions.find(s => s.value === entry.status);
                
                return (
                  <div key={entry.id} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onCheckedChange={() => toggleEntrySelection(entry.id)}
                          />
                          <h4 className="text-lg font-semibold text-white">{entry.title}</h4>
                          <Badge className={`${priorityOption?.color} bg-transparent border`}>
                            {priorityOption?.label}
                          </Badge>
                          <Badge className={`${statusOption?.color} bg-transparent border`}>
                            {statusOption?.label}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-300 mb-3">{entry.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
                          {entry.timestamp && (
                            <div>
                              <span className="font-medium">Timestamp:</span> {format24HourTimestamp(entry.timestamp)}
                            </div>
                          )}
                          {entry.location && (
                            <div>
                              <span className="font-medium">Location:</span> {entry.location}
                            </div>
                          )}
                          {entry.incident_name && (
                            <div>
                              <span className="font-medium">Incident:</span> {entry.incident_name}
                            </div>
                          )}
                          {entry.site_safety_plan_required !== undefined && (
                            <div>
                              <span className="font-medium">Site Safety Plan Required:</span> {entry.site_safety_plan_required ? 'Yes' : 'No'}
                            </div>
                          )}
                        </div>
                        
                        {entry.notes && (
                          <div className="mt-3 p-3 bg-gray-600 rounded text-sm text-gray-300">
                            <span className="font-medium">Notes:</span> {entry.notes}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handlePrint(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(entry.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Location Management Dialog */}
      {showAddLocationDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg max-w-lg w-full mx-4 border border-gray-700">
            <h3 className="text-xl font-bold text-orange-500 mb-4">Manage Check-in Locations</h3>
            
            {/* Add New Location Section */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Add New Location</label>
                <div className="space-y-2 mt-2">
                  <input
                    value={newLocationName}
                    onChange={(e) => setNewLocationName(e.target.value)}
                    placeholder="Location name *"
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400"
                  />
                  <input
                    value={newLocationDescription}
                    onChange={(e) => setNewLocationDescription(e.target.value)}
                    placeholder="Description (optional)"
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddLocation();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={handleAddLocation}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Location
                  </Button>
                </div>
              </div>
            </div>

            {/* Existing Locations List */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-white mb-2">Select or Manage Locations</label>
              {formData.location && (
                <div className="mb-2 p-2 bg-blue-600 rounded text-white text-sm flex items-center justify-between">
                  <span>Current Selection: <strong>{formData.location}</strong></span>
                  <CheckCircle className="h-4 w-4" />
                </div>
              )}
              <div className="bg-gray-900 rounded-lg p-3 max-h-60 overflow-y-auto">
                {/* Default Locations (Cannot Delete, Can Select) */}
                {defaultLocations.map((location) => (
                  <div 
                    key={location} 
                    className={`flex items-center justify-between py-2 px-3 rounded cursor-pointer transition-colors ${
                      formData.location === location 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-gray-700'
                    }`}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, location }));
                    }}
                  >
                    <span className="text-white">{location}</span>
                    <div className="flex items-center gap-2">
                      {formData.location === location && (
                        <CheckCircle className="h-4 w-4 text-white" />
                      )}
                      <span className="text-xs text-gray-500">Default</span>
                    </div>
                  </div>
                ))}
                
                {/* Custom Locations (Can Delete and Select) */}
                {locations.map((location) => (
                  <div 
                    key={location.id} 
                    className={`flex items-center justify-between py-2 px-3 rounded group transition-colors ${
                      formData.location === location.name 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'hover:bg-gray-700'
                    }`}
                  >
                    <span 
                      className="text-white flex-1 cursor-pointer"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, location: location.name }));
                      }}
                    >
                      {location.name}
                    </span>
                    <div className="flex items-center gap-2">
                      {formData.location === location.name && (
                        <CheckCircle className="h-4 w-4 text-white" />
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLocation(location.id, location.name);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-600 rounded transition-all"
                        title="Delete location"
                      >
                        <Trash className="h-4 w-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                ))}
                
                {locations.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">No custom locations yet</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => {
                  setShowAddLocationDialog(false);
                  setNewLocationName('');
                  setNewLocationDescription('');
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ICS 204 - Assignment List Component

export default SafetyModule;
